/* eslint-disable react/jsx-no-target-blank */
import React, { useRef } from 'react';
import classNames from 'classnames';
import { useHover } from 'usehooks-ts';
import HotelCardGalleryOverlay from './components/HotelCardGalleryOverlay';
import HotelCardFeatures from './components/HotelCardFeatures';
import HotelCardPrice from './components/HotelCardPrice';
import HotelCardLabels from './components/HotelCardLabels';
import HotelCardHeaderLabel from './components/HotelCardHeaderLabel';
import HotelCardAdjacentTours from './components/HotelCardAdjacentTours';
import type { HotelCardProps } from './types';
import HotelCardPenalizedFilters from './components/HotelCardPenalizedFilters';
import { HOTEL_CARD_HOTEL_INFO, HOTEL_CARD_TEST_ID } from './constants';
import useLongHover from './hooks/useLongHover';
import styles from './hotelCardSmall.module.css';

const HotelCardSmall: React.FC<HotelCardProps> = (props) => {
  const {
    id,
    images,
    bonusCount,
    currencyCode,
    hotelPageVisited,
    stars,
    hotelName,
    hotelLocation,
    rating,
    reviewsCount,
    cashbackIdentifier,
    customCashbackIconURL,
    customCashbackText,
    customCashbackTooltipText,
    features,
    instantConfirm,
    isFlexDates,
    price,
    innerLabels,
    headerLabel,
    isHotelOnly,
    availability,
    adjacentTours = [],
    datesInfo,
    penalizedFilters = [],
    hotelPageHref,
    openInCurrentTab,
    className,
    onLongHover,
  } = props;

  const cardRef = useRef<HTMLDivElement>(null);
  const isCardHovered = useHover(cardRef);

  useLongHover(isCardHovered, onLongHover);

  return (
    <div
      ref={cardRef}
      data-testid={HOTEL_CARD_TEST_ID}
      className={classNames(styles.cardContainer, className)}
    >
      <HotelCardHeaderLabel headerLabel={headerLabel} />
      <HotelCardGalleryOverlay
        id={id}
        price={price}
        images={images}
        isMobile
        bonusCount={bonusCount}
        currencyCode={currencyCode}
        hotelPageVisited={hotelPageVisited}
        stars={stars}
        hotelName={hotelName}
        hotelLocation={hotelLocation}
        rating={rating}
        reviewsCount={reviewsCount}
        cashbackIdentifier={cashbackIdentifier}
        customCashbackIconURL={customCashbackIconURL}
        customCashbackText={customCashbackText}
        customCashbackTooltipText={customCashbackTooltipText}
        hotelPageHref={hotelPageHref}
        openInCurrentTab={openInCurrentTab}
      />
      <div
        className={classNames(styles.hotelInfo, {
          [styles.hotelInfoNoPaddingBottom]:
            !adjacentTours.length && !penalizedFilters.length,
        })}
      >
        <a
          data-testid={HOTEL_CARD_HOTEL_INFO}
          href={hotelPageHref}
          target={openInCurrentTab ? '_self' : '_blank'}
          className={styles.linkToHotel}
        >
          <HotelCardFeatures isMobile features={features} />
        </a>
        <HotelCardLabels isMobile labels={innerLabels} />
        <a
          href={hotelPageHref}
          target={openInCurrentTab ? '_self' : '_blank'}
          className={styles.linkToHotel}
        >
          <HotelCardPrice
            availability={availability}
            instantConfirm={instantConfirm}
            isMobile
            isFlexDates={isFlexDates}
            isMapCard={false}
            price={price}
            currencyCode={currencyCode}
            isHotelOnly={isHotelOnly}
            datesInfo={datesInfo}
          />
        </a>
        <HotelCardAdjacentTours
          isFlexDates={isFlexDates}
          adjacentTours={adjacentTours}
          currencyCode={currencyCode}
          isHotelOnly={isHotelOnly}
          isMobile
        />
        {penalizedFilters.length ? (
          <a
            href={hotelPageHref}
            target={openInCurrentTab ? '_self' : '_blank'}
            className={styles.linkToHotel}
          >
            <HotelCardPenalizedFilters
              isMobile
              penalizedFilters={penalizedFilters}
            />
          </a>
        ) : null}
      </div>
    </div>
  );
};

export default HotelCardSmall;
